import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  Tooltip,
  Chip,
  CssBaseline,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import axiosInstance, {
  getLocalAccessToken,
} from "../common/services/axiosInstance";
import testBackground from "../assets/stacked-waves-haikei.svg";
import { useCharactersContext } from "../hooks/Context";
import RunningWithErrorsIcon from "@mui/icons-material/RunningWithErrors";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import UpdateIcon from "@mui/icons-material/Update";

interface Lesson {
  examId: number;
  classId: number;
  name: string;
  examinationId: number;
  subjectId: number;
}

interface QuestionData {
  examId: number;
  active: boolean;
  name: string;
  classId: number;
}

interface ActiveQuestionPaper {
  [key: number]: { active: boolean };
}

const Lessons = () => {
  const { setIsViewMode, completedLessons, setCompletedLessons } =
    useCharactersContext();
  const studentId = Number(localStorage.getItem("studentId"));
  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");
  const { moduleId, levelId } = useParams();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [examParticipation, setExamParticipation] = useState<any[]>([]);
  const [activeLessons, setActiveLessons] = useState<{
    [key: number]: { active: boolean };
  }>({});

  useEffect(() => {
    axiosInstance
      .get("/api/examinationParticipation/exam/get", {
        params: {
          classId: levelId,
          active: true,
        },
      })
      .then((response) => {
        setLessons(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [levelId]);

  useEffect(() => {
    axiosInstance
      .get("/api/examinationParticipation/examParticipation/get", {
        params: {
          studentId: studentId,
          classId: levelId,
        },
      })
      .then((response) => {
        const filteredExamParticipation = response.data;
        console.log("Filtered Exam Participation", filteredExamParticipation);
        setExamParticipation(filteredExamParticipation);
        setCompletedLessons(filteredExamParticipation.length);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    axiosInstance
      .get("/api/question/questionPaper/list", {
        params: {
          active: true,
        },
      })
      .then((response) => {
        if (Array.isArray(response.data.responseData)) {
          const allQuestionData: QuestionData[] = response.data.responseData;
          // Create a mapping between examId and an object with name and active status
          const activeQuestionPaperMap: {
            [key: number]: { active: boolean };
          } = {};

          allQuestionData.forEach((question) => {
            activeQuestionPaperMap[question.examId] = {
              active: question.active,
            };
          });
          setActiveLessons(activeQuestionPaperMap);
        } else {
          console.error("Failed to get questionPaper list");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [levelId]);

  let isLessonActive = (examId: number) => {
    return studentId === 1
      ? activeLessons.hasOwnProperty(examId)
      : activeLessons[examId]?.active === true;
  };

  let isExamCompleted = (examId: number) => {
    return examParticipation.some(
      (participation) => participation.examId === examId
    );
  };

  return (
    <div
      style={{
        // background: "linear-gradient(to top, #D9F6FF, #FFFFFF)",
        minHeight: "100vh",
        backgroundImage: `url(${testBackground})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: isMobile ? "15.5%" : isTablet ? "8%" : "10vh",
          marginBottom: "5vh",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: isMobile || isTablet ? "5vh" : "7vh",
            flexBasis: "100%",
            flexShrink: 2,
            textAlign: "left",
            fontWeight: "bold",
          }}
        >
          Lessons
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          marginLeft: isMobile ? "5%" : "2.5%",
          marginTop: "1.5vh",
        }}
      >
        <IconButton
          sx={{
            borderRadius: "50%",
            boxShadow: "10",
            // height: "3vh",
            "&.MuiIconButton-root": {
              backgroundColor: "primary.main",
              color: "white !important",
            },
          }}
          title="Navigate to Levels"
          onClick={() => {
            navigate(-1);
          }}
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        xs={12}
        style={{ height: "80vh", marginTop: "1vh" }}
      >
        {lessons
          .slice()
          .sort((a, b) => a.examId - b.examId)
          .map((lesson) => (
            <Grid item key={lesson.examId} xs={6} sm={4}>
              <div
              // style={{
              //   pointerEvents: isExamCompleted(lesson.examId)
              //     ? "none"
              //     : "auto",
              // }}
              >
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    marginLeft: "7%",
                    // marginRight: isMobile ? "3%" : "1%",
                    width: "auto",
                    height: "auto",
                    // boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                    cursor: "pointer",
                    // cursor: isExamCompleted(lesson.examId)
                    //   ? "default"
                    //   : "pointer",
                    // marginBottom: "1vh",
                    // backgroundColor: "primary.main",
                    backgroundColor: isExamCompleted(lesson.examId)
                      ? "grey" // Change the color for completed lessons
                      : "primary.main",
                    color: "white",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                  }}
                  onClick={() => {
                    if (isExamCompleted(lesson.examId)) {
                      setIsViewMode(true);
                    } else {
                      setIsViewMode(false);
                    }
                    navigate(
                      `/modules/${moduleId}/levels/${levelId}/lessons/${lesson.examId}`
                    );
                  }}
                >
                  {isExamCompleted(lesson.examId) &&
                    (isMobile || isTablet ? (
                      <Tooltip title="Homework of this lesson is completed">
                        <CheckCircleOutlineRoundedIcon
                          sx={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            // color: "secondary.main",
                            color: "#66bb6a",
                            fontSize: isMobile || isTablet ? "1.3em" : "auto",
                            bgcolor: "#ffffff", // white background
                            borderRadius: "50%", // round shape
                            p: 0.1, // padding
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<CheckCircleOutlineRoundedIcon />}
                        label="Completed"
                        title="Homework of this lesson is completed"
                        sx={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          // color: "secondary.main",
                          color: "white",
                          fontSize: isMobile ? "1.3em" : "auto",
                        }}
                        color="success"
                        size="small"
                      />
                    ))}

                  {!isExamCompleted(lesson.examId) &&
                    isLessonActive(lesson.examId) &&
                    (isMobile || isTablet ? (
                      <Tooltip title="Homework of this lesson is pending">
                        <RunningWithErrorsIcon
                          sx={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            // color: "secondary.main",
                            color: "#ffb74d",
                            fontSize: isMobile || isTablet ? "1.3em" : "auto",
                            bgcolor: "#ffffff", // white background
                            borderRadius: "50%", // round shape
                            p: 0.1, // padding
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<RunningWithErrorsIcon />}
                        label="Pending"
                        title="Homework of this lesson is Pending"
                        sx={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          // color: "secondary.main",
                          color: "white",
                          fontSize: isMobile || isTablet ? "1.3em" : "auto",
                        }}
                        color={
                          isLessonActive(lesson.examId) ? "warning" : "info"
                        }
                        size="small"
                      />
                    ))}

                  {!isExamCompleted(lesson.examId) &&
                    !isLessonActive(lesson.examId) &&
                    (isMobile || isTablet ? (
                      <Tooltip title="Homework of this lesson is Upcoming">
                        <UpdateIcon
                          sx={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            // color: "secondary.main",
                            color: "#ba68c8",
                            fontSize: isMobile || isTablet ? "1.3em" : "auto",
                            bgcolor: "#ffffff", // white background
                            borderRadius: "50%", // round shape
                            p: 0.1, // padding
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Chip
                        icon={<UpdateIcon />}
                        label="Upcoming"
                        title="Homework of this lesson is Upcoming"
                        sx={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          // color: "secondary.main",
                          color: "#2196f3",
                          fontSize: isMobile || isTablet ? "1.3em" : "auto",
                        }}
                        color={
                          !isLessonActive(lesson.examId) ? "info" : "warning"
                        }
                        size="small"
                      />
                    ))}

                  <CardContent>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "900",
                        textAlign: isMobile ? "left" : "center",
                        fontSize: isMobile ? "4vw" : "auto",
                      }}
                    >
                      {lesson.name}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
            </Grid>
          ))}
      </Grid>
    </div>
  );
};

export default Lessons;
